import React from 'react';
import { Modal } from 'antd';
import {
  AimOutlined,
  PlayCircleOutlined,
  RedoOutlined,
  StopOutlined,
  ReadOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';

import { API_URL } from 'settings';
import { DevAsset } from '../types';

/**
 * Full URL of a served Development-Plan asset. Served from the pm.api origin
 * (a separate origin from pm.web) via the /api/devplan-assets/ route (§5.4a) —
 * the iframe is cross-origin by design, which is what gives the postMessage
 * bridge a real, checkable `event.origin`. `encodeURI` handles the spaces in
 * dir names like "Idea Generator".
 */
export const assetUrl = (asset: DevAsset): string => {
  // Pass the parent (pm.web) origin so the instrumented HTML can postMessage back
  // to exactly this origin — robust to 127.0.0.1 vs localhost during smoke testing.
  const parent = encodeURIComponent(window.location.origin);
  // The backend emits `file_path` already resolved for this tree's asset rev. Today that's a
  // route-relative path (served via /devplan-assets/); at the CDN stage it becomes an absolute
  // URL — pass an absolute URL through unchanged.
  const fp = asset.file_path;
  const base = /^https?:\/\//.test(fp)
    ? fp
    : `${API_URL}/devplan-assets/${encodeURI(fp)}`;
  return `${base}?parentOrigin=${parent}`;
};

/** The pm.api origin the asset iframe loads from (for the bridge origin check). */
export const assetOrigin = (): string => {
  try {
    return new URL(API_URL, window.location.href).origin;
  } catch {
    return '';
  }
};

/** One resolved remediation drill for the in-modal "before you re-run" bar. The
 *  parent resolves the target key → asset/node so the bar just renders + acts. */
export interface PlayerRemediationItem {
  gate_key: string;
  gate_name: string;
  targetAsset?: DevAsset; // game drill (may be unbuilt)
  targetNodeCode?: string; // node to revisit
  targetNodeName?: string;
}

/** One selectable reference ladder in the workspace (a node's warm-up for one rank). */
export interface WorkspaceLadderOption {
  rank: number;
  asset: DevAsset;
  done: boolean;
}

type Props = {
  /** Primary asset: the single asset (game/drill) OR the scenario pane in workspace mode. */
  asset: DevAsset | null;
  /** Reference ladder — when present (and built) the modal renders the two-pane rank workspace. */
  ladder?: DevAsset | null;
  /** Initial split emphasis when the workspace opens. */
  workspaceFocus?: 'scenario' | 'ladder';
  /** The node's ladders (built) → drives the ladder-pane rank selector when there is >1. */
  ladderOptions?: WorkspaceLadderOption[];
  /** Node context for the workspace header + modal title (workspace mode only). */
  context?: {
    code: string;
    name: string;
    rank: number;
    maxRank: number;
  } | null;
  /** Failed-gate drills for the OPEN scenario (on-fail; empty otherwise). */
  remediation?: PlayerRemediationItem[];
  onClose: () => void;
  /** Swap the window to a game/drill (single-iframe mode). */
  onOpenDrill?: (asset: DevAsset) => void;
  /** Close the window and jump to a node to revisit. */
  onGoToNode?: (nodeCode: string) => void;
  /** Switch the reference ladder (rank selector). */
  onSelectLadder?: (asset: DevAsset) => void;
};

/** One cross-origin asset iframe. Kept mounted across layout changes (split/collapse/tab
 *  toggles are CSS only) so neither pane ever reloads — that is what preserves play state. */
const AssetFrame: React.FC<{ asset: DevAsset }> = ({ asset }) => (
  <iframe
    className="devplan-player__frame"
    title={asset.title}
    src={assetUrl(asset)}
  />
);

/** The docked "before you re-run" remediation bar (post-fail). */
const RemediationBar: React.FC<{
  items: PlayerRemediationItem[];
  onOpenDrill?: (asset: DevAsset) => void;
  onGoToNode?: (nodeCode: string) => void;
}> = ({ items, onOpenDrill, onGoToNode }) => (
  <div className="devplan-playerbar">
    <div className="devplan-playerbar__head">
      <AimOutlined /> Before you re-run — fix what you missed
    </div>
    <div className="devplan-playerbar__items">
      {items.map(it => (
        <div key={it.gate_key} className="devplan-playerbar__item">
          <span className="devplan-playerbar__gate">{it.gate_name}</span>
          {it.targetAsset && it.targetAsset.is_built && (
            <button
              type="button"
              className="devplan-playerbar__go"
              onClick={() => onOpenDrill?.(it.targetAsset as DevAsset)}
            >
              <PlayCircleOutlined /> {it.targetAsset.title}
            </button>
          )}
          {it.targetAsset && !it.targetAsset.is_built && (
            <span className="devplan-playerbar__go is-disabled">
              <StopOutlined /> {it.targetAsset.title} · not built yet
            </span>
          )}
          {!it.targetAsset && it.targetNodeCode && (
            <button
              type="button"
              className="devplan-playerbar__go"
              onClick={() => onGoToNode?.(it.targetNodeCode as string)}
            >
              <RedoOutlined /> {it.targetNodeName || it.targetNodeCode}
            </button>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** Hosts a scenario/game HTML in a cross-origin iframe.
 *
 *  Two modes:
 *  - SINGLE (games, remediation drills, a rank with no ladder): one iframe, plus the
 *    docked "before you re-run" bar when the last play failed a gate.
 *  - WORKSPACE (a rank that has a ladder): the scenario + its ladder are BOTH mounted in a
 *    resizable split — scenario primary, ladder as a collapsible reference pane. Both frames
 *    stay mounted so glancing at the ladder never reloads the scenario. Below a narrow width
 *    the split becomes tabs (still both mounted). The `{asset && …}` guard unmounts on close.
 */
const AssetPlayerModal: React.FC<Props> = ({
  asset,
  ladder,
  workspaceFocus = 'scenario',
  ladderOptions,
  context,
  remediation,
  onClose,
  onOpenDrill,
  onGoToNode,
  onSelectLadder,
}) => {
  const items = remediation ?? [];
  const isWorkspace = !!asset && !!ladder && !!ladder.is_built;

  // Split % for the scenario pane (wide mode), which pane is collapsed to a rail, the
  // active tab (narrow mode) and whether we are narrow. Reset when the workspace target
  // changes (a different scenario/ladder/focus reopens a fresh layout).
  const [split, setSplit] = React.useState(55);
  const [ladderCollapsed, setLadderCollapsed] = React.useState(false);
  const [tab, setTab] = React.useState<'scenario' | 'ladder'>('scenario');
  const [narrow, setNarrow] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef(false);

  // Reset layout only when a genuinely new workspace opens (scenario or focus) — NOT when the
  // reference ladder switches (rank selector), so switching ranks doesn't jar the split/tab.
  const wsKey = `${asset?.key ?? ''}|${workspaceFocus}`;
  React.useEffect(() => {
    if (!isWorkspace) return;
    setLadderCollapsed(false);
    // split = LEFT (warm-up) pane width. Open at 55% warm-up / 45% scenario (both entry paths).
    setSplit(55);
    setTab(workspaceFocus === 'ladder' ? 'ladder' : 'scenario');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsKey, isWorkspace]);

  // Track the body width so a cramped split falls back to tabs (both frames stay mounted).
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      setNarrow(w > 0 && w < 900);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isWorkspace]);

  // Divider drag (wide mode only). Pointer Events + setPointerCapture so the drag keeps tracking
  // even when the cursor is over an iframe: cross-origin iframes swallow document mouse events
  // (the frame eats mousemove/mouseup), which is what made the old divider stick to the cursor.
  // Capture routes every pointer event to the divider until release.
  const onDragDown = React.useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* capture unsupported — drag still works over non-iframe area */
    }
    draggingRef.current = true;
    setDragging(true);
  }, []);
  const onDragMove = React.useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const el = bodyRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setSplit(Math.min(78, Math.max(28, pct)));
  }, []);
  const onDragUp = React.useCallback((e: React.PointerEvent) => {
    draggingRef.current = false;
    setDragging(false);
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const renderWorkspace = () => {
    const showTabs = narrow;
    const collapsed = ladderCollapsed && !showTabs;
    const scenarioVisible = !showTabs || tab === 'scenario';
    const ladderVisible = !showTabs || tab === 'ladder';
    // `split` is the LEFT (warm-up) pane's width %; the scenario fills the rest on the right.
    const ladderStyle: React.CSSProperties =
      showTabs || collapsed ? {} : { width: `${split}%` };
    const rankProgress = context ? ` (${context.rank}/${context.maxRank})` : '';

    // Warm-up rank selector — lifted into the context header (the pane below is just the iframe).
    const rankPills =
      ladderOptions && ladderOptions.length > 1 ? (
        <span className="devplan-ws__rank-pick">
          {ladderOptions.map(o => (
            <button
              type="button"
              key={o.asset.key}
              className={`devplan-ws__rank-btn${
                o.asset.key === ladder?.key ? ' is-on' : ''
              }`}
              onClick={() => onSelectLadder?.(o.asset)}
              title={`Rank ${o.rank} warm-up${o.done ? ' (done)' : ''}`}
            >
              {o.rank}
              {o.done ? ' ✓' : ''}
            </button>
          ))}
        </span>
      ) : null;

    return (
      <div
        className={`devplan-workspace${showTabs ? ' is-narrow' : ''}${
          collapsed ? ' is-collapsed' : ''
        }${dragging ? ' is-dragging' : ''}`}
        ref={bodyRef}
      >
        {/* Context header — frames the two sides (warm-up ▶ rank) + carries the warm-up controls. */}
        {showTabs ? (
          <>
            <div className="devplan-ws-ctx devplan-ws-ctx--narrow">
              <span className="devplan-ws-ctx__lead">
                <ReadOutlined /> Warm-ups prep you for each rank — do them
                first; they stay open while you play.
              </span>
              {rankPills}
            </div>
            <div className="devplan-workspace__tabs">
              <button
                type="button"
                className={`devplan-ws-tab${tab === 'scenario' ? ' is-on' : ''}`}
                onClick={() => setTab('scenario')}
              >
                <PlayCircleOutlined /> Scenario
              </button>
              <button
                type="button"
                className={`devplan-ws-tab${tab === 'ladder' ? ' is-on' : ''}`}
                onClick={() => setTab('ladder')}
              >
                <ReadOutlined /> Warm-up
              </button>
            </div>
          </>
        ) : (
          <div className="devplan-ws-ctx">
            {!collapsed && (
              <div className="devplan-ws-ctx__warm" style={ladderStyle}>
                <div className="devplan-ws-ctx__row">
                  <span className="devplan-ws-ctx__chip">Warm-up</span>
                  {rankPills}
                  <button
                    type="button"
                    className="devplan-ws__collapse"
                    onClick={() => setLadderCollapsed(true)}
                    title="Hide the warm-up — give the scenario full width"
                  >
                    <LeftOutlined /> Hide
                  </button>
                </div>
                <div className="devplan-ws-ctx__sub">
                  Quick prep for the rank — best done first. Stays open while
                  you play the rank.
                </div>
              </div>
            )}
            <div className="devplan-ws-ctx__rank">
              <div className="devplan-ws-ctx__row">
                {!collapsed && (
                  <span className="devplan-ws-ctx__arrow">
                    Warm-ups prepare you for&nbsp;
                    <RightOutlined />
                  </span>
                )}
                <span className="devplan-ws-ctx__chip is-rank">The ranks</span>
              </div>
              <div className="devplan-ws-ctx__sub">
                The node&rsquo;s objective — complete it to rank up
                {rankProgress}.
              </div>
            </div>
          </div>
        )}

        <div className="devplan-workspace__body">
          {/* Warm-up pane (LEFT) — rail + iframe; its controls live in the context header.
              The frame is ALWAYS mounted; collapse + narrow-tab toggles are CSS only, so the
              ladder iframe never reloads. */}
          <div
            className={`devplan-ws__pane devplan-ws__pane--ladder${
              collapsed ? ' is-rail' : ''
            }`}
            style={{
              ...ladderStyle,
              display: ladderVisible ? undefined : 'none',
            }}
          >
            <button
              type="button"
              className="devplan-ws__rail"
              onClick={() => setLadderCollapsed(false)}
              title="Show the warm-up reference"
            >
              <RightOutlined /> <span>Warm-up</span>
            </button>
            <div className="devplan-ws__frame-wrap">
              <AssetFrame asset={ladder as DevAsset} />
            </div>
          </div>

          {/* Divider (wide, not collapsed) */}
          {!showTabs && !collapsed && (
            <div
              className="devplan-ws__divider"
              onPointerDown={onDragDown}
              onPointerMove={onDragMove}
              onPointerUp={onDragUp}
              role="separator"
              aria-orientation="vertical"
            />
          )}

          {/* Scenario pane (RIGHT) — fills the remaining width. */}
          <div
            className="devplan-ws__pane devplan-ws__pane--scenario"
            style={{ display: scenarioVisible ? undefined : 'none' }}
          >
            <div className="devplan-ws__frame-wrap">
              <AssetFrame asset={asset as DevAsset} />
            </div>
            {items.length > 0 && (
              <RemediationBar
                items={items}
                onOpenDrill={onOpenDrill}
                onGoToNode={onGoToNode}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSingle = () => (
    <div className="devplan-player">
      <AssetFrame asset={asset as DevAsset} />
      {items.length > 0 && (
        <RemediationBar
          items={items}
          onOpenDrill={onOpenDrill}
          onGoToNode={onGoToNode}
        />
      )}
    </div>
  );

  const modalTitle =
    isWorkspace && context ? (
      <span className="devplan-ws-title">
        {context.code} · {context.name}
        <span className="devplan-ws-title__badge">
          rank {context.rank}/{context.maxRank}
        </span>
      </span>
    ) : (
      asset?.title
    );

  return (
    <Modal
      title={modalTitle}
      open={!!asset}
      onCancel={onClose}
      footer={null}
      width="92vw"
      style={{ top: 20 }}
      styles={{ body: { padding: 0, height: '82vh' } }}
    >
      {asset && (isWorkspace ? renderWorkspace() : renderSingle())}
    </Modal>
  );
};

export default AssetPlayerModal;
